import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  white,
  gray100,
  primaryButtonBackgroundColor,
  gray40,
  white_overlay80,
  gray80,
} from '../../constants/colors';
import StaffListCard from '../uiParts/StaffListCard';
import { AuthContext } from '../../contexts/auth';
import { getAllUsers, getUser } from '../../api/firebase/firestore/user';
import SideBar from '../uiParts/SideBar';
import { registerStatus, userRole } from '../../constants/enum';
import MetaTitle from '../utils/MetaTitle';
import CustomSnackbar from '../uiParts/CustomSnackbar';
import CustomLoading from '../uiParts/CustomLoading';
import { getAllGroups } from '../../api/firebase/firestore/group';
import type { GroupType } from '../../validation/group/groupType';

const styles = {
  page: {
    height: '100%',
    display: 'flex',
    paddingBottom: '236px',
    '@media (max-width: 960px)': {
      flexDirection: 'column',
      paddingTop: '0',
      justifyContent: 'flex-start',
      paddingBottom: '100px',
    },
  },
  pageBox: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    marginLeft: '240px',
    height: '80px',
    textAlign: 'center',
    backgroundColor: white,
    borderBottom: `1px solid ${gray40}`,
    Padding: '24px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 24px',
    '@media (max-width: 960px)': {
      height: '41px',
      marginTop: '48px',
      padding: '0 10px 0 16px',
      borderTop: `1px solid ${gray40}`,
      marginLeft: '0',
    },
  },
  mobileInviteButtonSpace: {
    '@media (max-width: 960px)': {
      borderTop: `1px solid ${gray40}`,
      marginLeft: '0',
    },
  },
  headerTitle: {
    fontSize: '24px',
    fontWeight: 700,
    '@media (max-width: 960px)': {
      fontSize: '16px',
    },
  },
  container: {
    marginLeft: '240px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '24px',
    paddingRight: '16px',
    paddingLeft: '16px',
    width: '-webkit-fill-available',
    '@media (max-width: 960px)': {
      height: '100%',
      alignItems: 'flex-start',
      marginLeft: '0',
      paddingRight: '0',
      paddingLeft: '0',
    },
  },
  sidebar: {
    position: 'fixed',
    color: 'white',
    textAlign: 'center',
    backgroundColor: gray100,
    width: '240px',
    height: '100vh',
    flexShrink: 0,
    zIndex: 1000,
    '@media (max-width: 960px)': {
      width: '100%',
      minWidth: '240px',
      height: '48px',
    },
  },
  button: {
    border: 'none',
    fontWeight: 700,
    fontSize: '16px',
    textAlign: 'center',
    width: '192px',
    height: '44px',
    '@media (max-width: 960px)': {
      width: '100%',
    },
  },
  notDisplayMobile: {
    '@media (max-width: 960px)': {
      display: 'none',
    },
  },
  mobileStaffInviteButton: {
    display: 'none',
    '@media (max-width: 960px)': {
      display: 'block',
    },
    '@media screen and (max-width:960px)': {
      boxSizing: 'border-box',
      position: 'fixed',
      bottom: 0,
      left: 0,
      backgroundColor: white_overlay80,
      padding: '16px 16px 24px',
      width: '100%',
      zIndex: 1,
    },
    '@media screen and (max-width:600px)': {
      flexDirection: 'column',
      gap: '12px',
    },
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  userCountMessage: {
    fontWeight: 400,
    fontSize: '16px',
    fontStyle: 'normal',
    color: gray80,
    textAlign: 'right',
    '@media (max-width: 960px)': {
      fontSize: '14px',
    },
  },
};

type User = {
  uid: string;
  email: string;
  role: number;
  department: string;
  inviteFlg: boolean;
  name: string;
  icon: string;
  registerStatus: number;
};

const InviteButton = ({
  forPc,
}: {
  forPc: boolean;
  registeredUsers: number;
  setErrorText: React.Dispatch<React.SetStateAction<string>>;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
}): ReactElement => {
  const navigate = useNavigate();

  return (
    <Button
      onClick={(): void => {
        navigate('/staff/invite');
      }}
      fullWidth
      variant="outlined"
      sx={[primaryButtonBackgroundColor, styles.button, forPc ? styles.notDisplayMobile : null]}
    >
      ＋ 担当者を招待する
    </Button>
  );
};

const isAdmin = (user: User): boolean => user.role === userRole.admin;

const Staff: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loginUser, setLoginUser] = useState<User | null>(null);
  const [groups, setGroups] = useState<GroupType[]>([]);
  const { user, isLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [errorText, setErrorText] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      setLoading(true);
      try {
        const [fetchedUsers, fetchedGroups] = await Promise.all([
          getAllUsers() as Promise<User[]>,
          getAllGroups().catch(() => [] as GroupType[]),
        ]);
        setUsers(fetchedUsers);
        setGroups(fetchedGroups);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (user !== null) {
      const fetchLoginUser = async (): Promise<void> => {
        const loginUser = await getUser(user?.uid);
        setLoginUser(loginUser as User);
      };

      fetchLoginUser();
    }
  }, [user]);

  useEffect(() => {
    if (isLoading) return;
    // 未ログイン状態の場合はログイン画面へ遷移
    if (user === null) navigate('/');
  }, [user, isLoading]);
  // testLoginUserがnullでない場合のみisAdminを呼び出す
  const isUserAdmin = loginUser ? isAdmin(loginUser) : false;

  const registeredUsers = users.filter(
    user =>
      user.registerStatus === registerStatus.finished ||
      user.registerStatus === registerStatus.emailRegistered,
  ).length;

  return (
    <>
      <MetaTitle />
      <CustomLoading loading={loading || loginUser === null} />
      <Box sx={styles.page}>
        <Box sx={styles.sidebar}>
          <SideBar />
        </Box>
        <Box sx={styles.pageBox}>
          <Box sx={styles.header}>
            <Typography sx={styles.headerTitle}>担当者一覧</Typography>
            {isUserAdmin ? (
              <>
                <Box sx={styles.headerContent}>
                  <InviteButton
                    forPc={true}
                    registeredUsers={registeredUsers}
                    setErrorText={setErrorText}
                    setSnackbarOpen={setSnackbarOpen}
                  />
                </Box>
              </>
            ) : null}
          </Box>
          {loginUser && (
            <Box sx={styles.container}>
              <StaffListCard
                isAdmin={isUserAdmin}
                staffList={users}
                loginUser={loginUser}
                groups={groups}
              />
            </Box>
          )}
        </Box>
        {isUserAdmin ? (
          <Box sx={[styles.mobileInviteButtonSpace, styles.mobileStaffInviteButton]}>
            <InviteButton
              forPc={false}
              registeredUsers={registeredUsers}
              setErrorText={setErrorText}
              setSnackbarOpen={setSnackbarOpen}
            />
          </Box>
        ) : null}
      </Box>
      <CustomSnackbar
        autoHideDuration={6000}
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
      />
    </>
  );
};

export default Staff;
